import React from 'react';
import { 
  CheckCircle, XCircle, AlertTriangle, Mail, 
  X, ChevronDown, ChevronUp 
} from 'lucide-react';

const BulkEmailResultModal = ({ isOpen, onClose, result }) => {
  const [showSkipped, setShowSkipped] = React.useState(false);
  const [showFailed, setShowFailed] = React.useState(false);

  if (!isOpen || !result) return null;

  const getStatusColor = () => {
    if (result.isFullSuccess) return 'text-green-600';
    if (result.failedCount > 0) return 'text-red-600';
    if (result.skippedCount > 0) return 'text-yellow-600';
    return 'text-blue-600';
  };

  const getStatusIcon = () => {
    if (result.isFullSuccess) return <CheckCircle className="h-12 w-12 text-green-500" />;
    if (result.failedCount > 0) return <XCircle className="h-12 w-12 text-red-500" />;
    if (result.skippedCount > 0) return <AlertTriangle className="h-12 w-12 text-yellow-500" />;
    return <Mail className="h-12 w-12 text-blue-500" />;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            Rezultate Trimitere Email-uri
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          {/* Status Icon & Message */}
          <div className="flex flex-col items-center mb-6">
            {getStatusIcon()}
            <p className={`text-lg font-medium mt-4 text-center ${getStatusColor()}`}>
              {result.message}
            </p>
          </div>

          {/* Statistics Summary */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-gray-50 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-gray-900">
                {result.totalProcessed}
              </div>
              <div className="text-sm text-gray-600 mt-1">Total Procesate</div>
            </div>

            <div className="bg-green-50 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-green-600">
                {result.successCount}
              </div>
              <div className="text-sm text-gray-600 mt-1">Trimise</div>
            </div>

            {result.failedCount > 0 && (
              <div className="bg-red-50 rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-red-600">
                  {result.failedCount}
                </div>
                <div className="text-sm text-gray-600 mt-1">Erori</div>
              </div>
            )}

            {result.skippedCount > 0 && (
              <div className="bg-yellow-50 rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-yellow-600">
                  {result.skippedCount}
                </div>
                <div className="text-sm text-gray-600 mt-1">Sărite</div>
              </div>
            )}
          </div>

          {/* Success Rate */}
          {result.totalProcessed > 0 && (
            <div className="mb-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700">
                  Rată de Succes
                </span>
                <span className="text-sm font-medium text-gray-900">
                  {result.successRate.toFixed(1)}%
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div
                  className={`h-3 rounded-full transition-all duration-500 ${
                    result.successRate === 100 
                      ? 'bg-green-500' 
                      : result.successRate >= 75 
                      ? 'bg-blue-500' 
                      : result.successRate >= 50 
                      ? 'bg-yellow-500' 
                      : 'bg-red-500'
                  }`}
                  style={{ width: `${result.successRate}%` }}
                />
              </div>
            </div>
          )}

          {/* Skipped Items */}
          {result.skippedItems && result.skippedItems.length > 0 && (
            <div className="mb-4">
              <button
                onClick={() => setShowSkipped(!showSkipped)}
                className="w-full flex items-center justify-between p-4 bg-yellow-50 rounded-lg hover:bg-yellow-100 transition-colors"
              >
                <div className="flex items-center">
                  <AlertTriangle className="h-5 w-5 text-yellow-600 mr-2" />
                  <span className="font-medium text-gray-900">
                    Elemente Sărite ({result.skippedItems.length})
                  </span>
                </div>
                {showSkipped ? (
                  <ChevronUp className="h-5 w-5 text-gray-600" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-gray-600" />
                )}
              </button>

              {showSkipped && (
                <div className="mt-2 space-y-2">
                  {result.skippedItems.map((item, index) => (
                    <div
                      key={index}
                      className="p-4 bg-white border border-yellow-200 rounded-lg"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="font-medium text-gray-900 mb-1">
                            {item.itemNumber}
                          </div>
                          <div className="text-sm text-gray-600 mb-1">
                            Client: {item.clientName}
                          </div>
                          <div className="text-sm text-yellow-700 flex items-start">
                            <AlertTriangle className="h-4 w-4 mr-1 flex-shrink-0 mt-0.5" />
                            {item.reason}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Failed Items */}
          {result.failedItems && result.failedItems.length > 0 && (
            <div className="mb-4">
              <button
                onClick={() => setShowFailed(!showFailed)}
                className="w-full flex items-center justify-between p-4 bg-red-50 rounded-lg hover:bg-red-100 transition-colors"
              >
                <div className="flex items-center">
                  <XCircle className="h-5 w-5 text-red-600 mr-2" />
                  <span className="font-medium text-gray-900">
                    Erori ({result.failedItems.length})
                  </span>
                </div>
                {showFailed ? (
                  <ChevronUp className="h-5 w-5 text-gray-600" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-gray-600" />
                )}
              </button>

              {showFailed && (
                <div className="mt-2 space-y-2">
                  {result.failedItems.map((item, index) => (
                    <div
                      key={index}
                      className="p-4 bg-white border border-red-200 rounded-lg"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="font-medium text-gray-900 mb-1">
                            {item.itemNumber}
                          </div>
                          <div className="text-sm text-gray-600 mb-1">
                            Client: {item.clientName}
                          </div>
                          <div className="text-sm text-red-700 flex items-start">
                            <XCircle className="h-4 w-4 mr-1 flex-shrink-0 mt-0.5" />
                            {item.errorMessage}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Success Message */}
          {result.isFullSuccess && (
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-start">
                <CheckCircle className="h-5 w-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-green-800">
                    Toate email-urile au fost trimise cu succes!
                  </p>
                  <p className="text-sm text-green-700 mt-1">
                    {result.successCount} email{result.successCount !== 1 ? '-uri' : ''} 
                    {' '}trimis{result.successCount !== 1 ? 'e' : ''} către clienți.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            Închide
          </button>
        </div>
      </div>
    </div>
  );
};

export default BulkEmailResultModal;